import { useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PageHeader from '../components/PageHeader';
import { useOrgSettings } from '../features/settings/hooks/useOrgSettings';
import { useNotification } from '../context/useNotification';
import { SETTINGS_PAGE_LABELS as LABELS, SETTINGS_TAB_LABELS as TAB_LABELS } from '../utils/labels';
import SettingsModal from '../features/settings/components/settings/SettingsModal';
import MachineSettingsModal from '../features/settings/components/settings/MachineSettingsModal';
import OrderEmailTemplateModal from '../features/settings/components/settings/OrderEmailTemplateModal';
import ReturnEmailTemplateModal from '../features/settings/components/settings/ReturnEmailTemplateModal';
import type { Settings } from '../features/settings/types';
import { hasFullAccess } from '../utils/roles';
import type { MemberRole } from '../types/roles';

interface SettingsPageProps {
  orgId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
}

export default function SettingsPage({ orgId, isSuperAdmin, role }: SettingsPageProps) {
  const canEdit = hasFullAccess(role, isSuperAdmin);
  const { settings, updateSettings } = useOrgSettings(orgId);
  const { notifySuccess, notifyError } = useNotification();
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [machineModalOpen, setMachineModalOpen] = useState(false);
  const [orderEmailModalOpen, setOrderEmailModalOpen] = useState(false);
  const [returnEmailModalOpen, setReturnEmailModalOpen] = useState(false);

  const handleSaveBookingTimes = async (
    values: Pick<Settings, 'default_start_time' | 'default_end_time'>
  ) => {
    const ok = await updateSettings(values);
    if (ok) notifySuccess(TAB_LABELS.bookingTimesSaveSuccess);
    else notifyError(TAB_LABELS.saveFailed);
    return ok;
  };

  const handleSaveMachineSettings = async (
    values: Pick<
      Settings,
      | 'default_machine_start_time'
      | 'default_machine_end_time'
      | 'machine_end_date_definitive'
      | 'machine_default_ordered'
    >
  ) => {
    const ok = await updateSettings(values);
    if (ok) notifySuccess(TAB_LABELS.machineSettingsSaveSuccess);
    else notifyError(TAB_LABELS.saveFailed);
    return ok;
  };

  const handleSaveOrderEmail = async (
    values: Pick<Settings, 'order_email_subject' | 'order_email_body'>
  ) => {
    const ok = await updateSettings(values);
    if (ok) notifySuccess(TAB_LABELS.orderEmailSaveSuccess);
    else notifyError(TAB_LABELS.saveFailed);
    return ok;
  };

  const handleSaveReturnEmail = async (
    values: Pick<Settings, 'return_email_subject' | 'return_email_body'>
  ) => {
    const ok = await updateSettings(values);
    if (ok) notifySuccess(TAB_LABELS.returnEmailSaveSuccess);
    else notifyError(TAB_LABELS.saveFailed);
    return ok;
  };

  return (
    <div className="h-dvh flex flex-col bg-background">
      <PageHeader />
      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-xl mx-auto flex flex-col gap-4">
          <h1 className="text-lg font-semibold">{LABELS.pageGeneral}</h1>
          <div className="rounded-2xl border border-border overflow-hidden bg-surface">
            <div className="p-6">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold mt-0">{TAB_LABELS.bookingTimesTitle}</p>
                  <p className="text-xs text-text-secondary mb-0 mt-0">
                    {TAB_LABELS.bookingTimesSubtitle}
                  </p>
                </div>
                {canEdit && (
                  <Tooltip title={TAB_LABELS.editButton}>
                    <IconButton
                      onClick={() => setBookingModalOpen(true)}
                      aria-label={TAB_LABELS.editButton + ' ' + TAB_LABELS.bookingTimesTitle}
                      size="small"
                      color="primary"
                      data-testid="edit-booking-times-button"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
              <div className="grid grid-cols-2 gap-6 mt-3">
                <div>
                  <p className="text-xs text-text-secondary mb-0 mt-0">
                    {TAB_LABELS.startTimeLabel}
                  </p>
                  <p className="font-medium mb-0 mt-0">{settings.default_start_time}</p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-0 mt-0">{TAB_LABELS.endTimeLabel}</p>
                  <p className="font-medium mb-0 mt-0">{settings.default_end_time}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border overflow-hidden bg-surface">
            <div className="p-6">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold mt-0">{TAB_LABELS.machineSettingsTitle}</p>
                  <p className="text-xs text-text-secondary mb-0 mt-0">
                    {TAB_LABELS.machineSettingsSubtitle}
                  </p>
                </div>
                {canEdit && (
                  <Tooltip title={TAB_LABELS.editButton}>
                    <IconButton
                      onClick={() => setMachineModalOpen(true)}
                      aria-label={TAB_LABELS.editButton + ' ' + TAB_LABELS.machineSettingsTitle}
                      size="small"
                      color="primary"
                      data-testid="edit-machine-settings-button"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
              <p className="text-xs text-text-secondary mb-1 mt-3 font-medium">
                {TAB_LABELS.machineTimesTitle}
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-text-secondary mb-0 mt-0">
                    {TAB_LABELS.machineStartTimeLabel}
                  </p>
                  <p className="font-medium mb-0 mt-0">{settings.default_machine_start_time}</p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-0 mt-0">
                    {TAB_LABELS.machineEndTimeLabel}
                  </p>
                  <p className="font-medium mb-0 mt-0">{settings.default_machine_end_time}</p>
                </div>
              </div>
              <div className="flex flex-col gap-2 mt-4">
                <div className="flex items-center gap-2">
                  {settings.machine_end_date_definitive ? (
                    <CheckIcon fontSize="small" className="text-primary" />
                  ) : (
                    <CloseIcon fontSize="small" className="text-text-secondary" />
                  )}
                  <p className="text-sm mb-0 mt-0">{TAB_LABELS.machineEndDateDefinitiveLabel}</p>
                </div>
                <div className="flex items-center gap-2">
                  {settings.machine_default_ordered ? (
                    <CheckIcon fontSize="small" className="text-primary" />
                  ) : (
                    <CloseIcon fontSize="small" className="text-text-secondary" />
                  )}
                  <p className="text-sm mb-0 mt-0">{TAB_LABELS.machineDefaultOrderedLabel}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border overflow-hidden bg-surface">
            <div className="p-6">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold mt-0">{TAB_LABELS.orderEmailTemplateTitle}</p>
                  <p className="text-xs text-text-secondary mb-0 mt-0">
                    {TAB_LABELS.orderEmailTemplateSubtitle}
                  </p>
                </div>
                {canEdit && (
                  <Tooltip title={TAB_LABELS.editButton}>
                    <IconButton
                      onClick={() => setOrderEmailModalOpen(true)}
                      aria-label={TAB_LABELS.editButton + ' ' + TAB_LABELS.orderEmailTemplateTitle}
                      size="small"
                      color="primary"
                      data-testid="edit-order-email-template-button"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
              <p className="text-sm mt-3 mb-0" data-testid="order-email-badge">
                {settings.order_email_subject === null
                  ? TAB_LABELS.orderEmailDefaultBadge
                  : TAB_LABELS.orderEmailCustomBadge}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-border overflow-hidden bg-surface">
            <div className="p-6">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold mt-0">{TAB_LABELS.returnEmailTemplateTitle}</p>
                  <p className="text-xs text-text-secondary mb-0 mt-0">
                    {TAB_LABELS.returnEmailTemplateSubtitle}
                  </p>
                </div>
                {canEdit && (
                  <Tooltip title={TAB_LABELS.editButton}>
                    <IconButton
                      onClick={() => setReturnEmailModalOpen(true)}
                      aria-label={TAB_LABELS.editButton + ' ' + TAB_LABELS.returnEmailTemplateTitle}
                      size="small"
                      color="primary"
                      data-testid="edit-return-email-template-button"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
              <p className="text-sm mt-3 mb-0" data-testid="return-email-badge">
                {settings.return_email_subject === null
                  ? TAB_LABELS.returnEmailDefaultBadge
                  : TAB_LABELS.returnEmailCustomBadge}
              </p>
            </div>
          </div>
        </div>
      </main>

      {bookingModalOpen && (
        <SettingsModal
          settings={settings}
          onClose={() => setBookingModalOpen(false)}
          onSave={handleSaveBookingTimes}
        />
      )}
      {machineModalOpen && (
        <MachineSettingsModal
          settings={settings}
          onClose={() => setMachineModalOpen(false)}
          onSave={handleSaveMachineSettings}
        />
      )}
      {orderEmailModalOpen && (
        <OrderEmailTemplateModal
          settings={settings}
          onClose={() => setOrderEmailModalOpen(false)}
          onSave={handleSaveOrderEmail}
        />
      )}
      {returnEmailModalOpen && (
        <ReturnEmailTemplateModal
          settings={settings}
          onClose={() => setReturnEmailModalOpen(false)}
          onSave={handleSaveReturnEmail}
        />
      )}
    </div>
  );
}
